import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TeacherOption } from 'src/app/services/models/subjects/subject.model';
import { SharedModule } from 'src/shared/shared.module';
import { LabelMode } from 'src/utils/label-mode.enum';
import {
  invalidProjectDescriptionTokens,
  parseProjectDescriptionSections,
  projectDescriptionTokens,
} from 'src/utils/project-description-sections';

declare const bootstrap: any;

interface ProjectDescriptionWarning {
  title: string;
  detail: string;
}

// Held as constants so the template binding keeps the same reference on every
// change detection pass.
const MISSING_DESCRIPTION_WARNING: ProjectDescriptionWarning = {
  title: 'ไม่พบคำอธิบายโครงงาน/วิจัย',
  detail:
    'หากไม่ระบุคำอธิบาย ระบบจะไม่สามารถระบุกลุ่มที่อาจารย์รับผิดชอบเพื่อใช้ในการคำนวณภาระงานสอนได้',
};

const INVALID_DESCRIPTION_FORMAT_WARNING: ProjectDescriptionWarning = {
  title: 'รูปแบบคำอธิบายโครงงาน/วิจัยไม่ถูกต้อง',
  detail:
    'กรุณาตรวจสอบและแก้ไขรูปแบบการกรอกข้อมูลให้ถูกต้อง เพื่อให้ระบบสามารถระบุกลุ่มที่อาจารย์รับผิดชอบ และนำข้อมูลไปคำนวณภาระงานสอนได้อย่างถูกต้อง',
};

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

@Component({
  selector: 'app-project-teacher-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './project-teacher-modal.component.html',
  styleUrl: './project-teacher-modal.component.scss',
})
export class ProjectTeacherModalComponent {
  @Input({ required: true }) projectTeacherForm!: FormGroup;
  @Input({ required: true }) projectTeachingTimes!: FormArray<FormGroup>;
  @Input() teacherOptions: TeacherOption[] = [];
  @Input() teachingDayOptions: string[] = [];
  @Input() timeOptions: string[] = [];
  @Input() projectTeacherSubmitted = false;
  @Input() isSaving = false;
  @Input() saveError = '';

  @Output() projectTeacherSelected = new EventEmitter<TeacherOption | undefined>();
  @Output() addTeachingTime = new EventEmitter<void>();
  @Output() removeTeachingTime = new EventEmitter<number>();
  @Output() teachingTimeChanged = new EventEmitter<void>();
  @Output() saveProjectTeacher = new EventEmitter<void>();
  @Output() cancelProjectTeacher = new EventEmitter<void>();

  @ViewChild('projectTeacherModal')
  private projectTeacherModal?: ElementRef<HTMLElement>;

  readonly LabelMode = LabelMode;

  open(): void {
    const modalEl = this.projectTeacherModal?.nativeElement;

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  close(): void {
    const modalEl = this.projectTeacherModal?.nativeElement;

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getInstance(modalEl)?.hide();
  }

  hasProjectTeacherError(controlName: string): boolean {
    const control = this.projectTeacherForm.get(controlName);

    return control != null && control.invalid && (this.projectTeacherSubmitted || control.touched);
  }

  hasProjectTeachingTimeError(time: FormGroup, controlName: string): boolean {
    const control = time.get(controlName);

    return control != null && control.invalid && (this.projectTeacherSubmitted || control.touched);
  }

  // ADR-EK: คำอธิบายคือที่มาของ "กลุ่ม" บนรายงานภาระการสอน (doc_3) สองเมธอดนี้จึงโชว์ผลลัพธ์ที่
  // ระบบจะอ่านได้จริง และเตือนสองกรณี (ไม่กรอกเลย / กรอกแล้วอ่านไม่ออก) โดยไม่บล็อกการบันทึก
  // ตามที่ผู้ใช้ตัดสิน
  projectDescriptionSections(): string[] {
    return parseProjectDescriptionSections(this.projectTeacherForm.get('description')?.value);
  }

  projectDescriptionWarning(): ProjectDescriptionWarning | null {
    const description = this.projectTeacherForm.get('description')?.value;

    if (projectDescriptionTokens(description).length === 0) {
      return MISSING_DESCRIPTION_WARNING;
    }

    if (invalidProjectDescriptionTokens(description).length > 0) {
      return INVALID_DESCRIPTION_FORMAT_WARNING;
    }

    return null;
  }

  getProjectTeachingTimesError(): string {
    if (this.projectTeachingTimes.hasError('timeOrder')) {
      return 'เวลาเริ่มต้นต้องน้อยกว่าเวลาสิ้นสุด';
    }

    if (this.projectTeachingTimes.hasError('timeOverlap')) {
      return 'วันเดียวกันห้ามมีช่วงเวลาสอนซ้อนกัน';
    }

    return '';
  }
}
